import {
    Button,
    Icon,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@elit-hair/elit-ui";
import parsePhoneNumber from "libphonenumber-js";
import { useEffect, useMemo, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { useTranslation } from "react-i18next";
import { IoEarthOutline } from "react-icons/io5";
import { TbChevronDown, TbRefresh } from "react-icons/tb";

import { cn } from "@/lib/utils";
import { useCall } from "@/providers/hooks/use-call";
import { usePhoneNumbers } from "@/queries/phone-numbers.queries";
import { IPhoneNumber } from "@/types/phone.types";
import { formatPhoneNumber } from "@/utils/format-phone-number";

type PhoneNumberSelectorProps = {
    /** Whether the sidebar is collapsed */
    isCollapsed?: boolean;
};

export const PhoneNumberSelector = ({ isCollapsed = false }: PhoneNumberSelectorProps) => {
    const [isPhoneNumberPopoverOpen, setIsPhoneNumberPopoverOpen] = useState(false);
    const { t } = useTranslation();
    const { callerPhoneNumber, setCallerPhoneNumber, callStatus, temporaryCallerPhoneNumber } = useCall();
    const { data: phoneNumbers, isLoading, error, refetch } = usePhoneNumbers();
    const [searchTerm, setSearchTerm] = useState("");

    const isCallActive = callStatus !== "disconnected";

    // Close popover when call becomes active
    useEffect(() => {
        if (isCallActive) {
            setIsPhoneNumberPopoverOpen(false);
            setSearchTerm("");
        }
    }, [isCallActive]);

    const handleSelectNumber = (number: IPhoneNumber | undefined) => {
        setCallerPhoneNumber(number);
        setSearchTerm("");
        setIsPhoneNumberPopoverOpen(false);
    };

    // Get the active phone number (temporary during call or selected)
    const getActivePhoneNumber = () => {
        // During active call, prioritize temporary number if available
        if (isCallActive) {
            const numberToUse = temporaryCallerPhoneNumber || callerPhoneNumber?.number;
            if (numberToUse) {
                try {
                    const parsed = parsePhoneNumber(numberToUse);
                    const countryCode = parsed?.country?.toLowerCase();
                    const formatted = formatPhoneNumber(numberToUse, "INTERNATIONAL", true);
                    return {
                        number: numberToUse,
                        formattedNumber: formatted,
                        countryCode,
                        name: callerPhoneNumber?.name || t("auto_number"),
                    };
                } catch {
                    return {
                        number: numberToUse,
                        formattedNumber: numberToUse,
                        countryCode: undefined,
                        name: callerPhoneNumber?.name || t("auto_number"),
                    };
                }
            }
        }
        // When no call active, use selected number
        if (callerPhoneNumber) {
            try {
                const countryCode = parsePhoneNumber(callerPhoneNumber.number)?.country?.toLowerCase();
                return {
                    number: callerPhoneNumber.number,
                    formattedNumber: formatPhoneNumber(callerPhoneNumber.number, "INTERNATIONAL", true),
                    countryCode,
                    name: callerPhoneNumber.name || t("auto_number"),
                };
            } catch {
                return {
                    number: callerPhoneNumber.number,
                    formattedNumber: formatPhoneNumber(callerPhoneNumber.number, "INTERNATIONAL", true),
                    countryCode: undefined,
                    name: callerPhoneNumber.name || t("auto_number"),
                };
            }
        }
        return null;
    };

    const activePhoneNumber = getActivePhoneNumber();

    // Get display text for selected phone number
    const getDisplayText = () => {
        if (!activePhoneNumber) {
            return t("auto_number");
        }
        return activePhoneNumber.name;
    };

    // Get display subtext for selected phone number
    const getDisplaySubtext = () => {
        if (!activePhoneNumber) {
            return t("number_will_be_selected_automatically");
        }
        return activePhoneNumber.formattedNumber;
    };

    // Filter phone numbers by selected brand and active status
    const availablePhoneNumbers = useMemo(
        () =>
            phoneNumbers
                ?.filter((phone) => phone.isActive)
                .sort((a, b) => {
                    const aName = a.name ?? "";
                    const bName = b.name ?? "";
                    return aName.localeCompare(bName);
                }) || [],
        [phoneNumbers]
    );

    const normalizePhoneNumberForSearch = (value: string) => value.replace(/\D/g, "");

    const filteredPhoneNumbers = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return availablePhoneNumbers;

        const queryDigits = normalizePhoneNumberForSearch(q);

        return availablePhoneNumbers.filter((phone) => {
            const name = phone.name?.toLowerCase() ?? "";
            const matchesName = name.includes(q);

            // Only attempt number matching if the query contains digits.
            const matchesNumber =
                queryDigits.length > 0 && normalizePhoneNumberForSearch(phone.number).includes(queryDigits);

            return matchesName || matchesNumber;
        });
    }, [availablePhoneNumbers, searchTerm]);

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <Popover
                    open={isPhoneNumberPopoverOpen && !isCallActive}
                    onOpenChange={(open) => {
                        if (!isCallActive) {
                            setIsPhoneNumberPopoverOpen(open);
                            if (!open) setSearchTerm("");
                        }
                    }}
                >
                    <PopoverTrigger asChild disabled={isCallActive}>
                        <SidebarMenuButton
                            className={cn(
                                "text-muted-foreground hover:bg-muted hover:text-foreground cursor-pointer py-5",
                                isCallActive && "opacity-50 cursor-not-allowed"
                            )}
                            tooltip={isCollapsed ? t("select_phone_number") : undefined}
                            disabled={isCallActive}
                        >
                            {activePhoneNumber ? (
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                        {activePhoneNumber.countryCode ? (
                                            <CircleFlag
                                                countryCode={activePhoneNumber.countryCode}
                                                className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                            />
                                        ) : (
                                            <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center shrink-0">
                                                <Icon icon={IoEarthOutline} />
                                            </div>
                                        )}
                                        <div className="flex flex-col items-start min-w-0 flex-1">
                                            <span className="font-medium truncate w-full">{getDisplayText()}</span>
                                            <span className="text-xs text-muted-foreground truncate w-full">
                                                {getDisplaySubtext()}
                                            </span>
                                        </div>
                                    </div>
                                    <Icon icon={TbChevronDown} size={20} className="shrink-0" />
                                </div>
                            ) : (
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <CircleFlag
                                            countryCode="eu"
                                            className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                        />
                                        <span>{t("auto_number")}</span>
                                    </div>
                                    <Icon icon={TbChevronDown} size={20} className="shrink-0" />
                                </div>
                            )}
                        </SidebarMenuButton>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2 overflow-hidden" align="start" sideOffset={5}>
                        <div className="flex flex-col">
                            <p className="text-sm text-muted-foreground opacity-50 p-2">{t("select_phone_number")}</p>
                            <div className="px-2 pb-1">
                                <Input
                                    type="text"
                                    inputMode="search"
                                    placeholder={t("search_by_name_number_status")}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    disabled={isCallActive}
                                    autoFocus={isPhoneNumberPopoverOpen && !isCallActive}
                                    className="h-8"
                                />
                            </div>
                            <div className="flex flex-col gap-1 max-h-[300px] overflow-y-auto">
                                {isLoading ? (
                                    <div className="flex justify-center items-center p-4"></div>
                                ) : error ? (
                                    <div className="text-destructive p-4 text-sm">{error.message}</div>
                                ) : (
                                    <>
                                        <Button
                                            variant="ghost"
                                            className={cn(
                                                "w-full justify-start gap-2 py-6 px-4 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer",
                                                callerPhoneNumber === undefined && "bg-muted text-foreground"
                                            )}
                                            onClick={() => handleSelectNumber(undefined)}
                                        >
                                            <CircleFlag
                                                countryCode="eu"
                                                className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                            />
                                            <p className="flex flex-col items-start min-w-0 flex-1">
                                                <span className="font-medium text-left">{t("auto_number")}</span>
                                                <span className="text-xs text-muted-foreground text-left">
                                                    {t("auto_selected_number")}
                                                </span>
                                            </p>
                                        </Button>
                                        {filteredPhoneNumbers.length > 0 ? (
                                            filteredPhoneNumbers.map((phoneNumber) => {
                                                let phoneCountryCode: string | undefined;
                                                try {
                                                    phoneCountryCode = parsePhoneNumber(
                                                        phoneNumber.number
                                                    )?.country?.toLowerCase();
                                                } catch {
                                                    phoneCountryCode = undefined;
                                                }
                                                const formattedNumber = formatPhoneNumber(
                                                    phoneNumber.number,
                                                    "INTERNATIONAL",
                                                    true
                                                );
                                                const isSelected = callerPhoneNumber?.id === phoneNumber.id;
                                                const isDisabled =
                                                    callStatus !== "disconnected" || !phoneNumber.isActive;

                                                return (
                                                    <Button
                                                        key={phoneNumber.id}
                                                        variant="ghost"
                                                        className={cn(
                                                            "w-full justify-start gap-2 py-6 px-4 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer",
                                                            isSelected && "bg-muted text-foreground",
                                                            isDisabled && "opacity-50 cursor-not-allowed"
                                                        )}
                                                        onClick={() => handleSelectNumber(phoneNumber)}
                                                        disabled={isDisabled}
                                                    >
                                                        {phoneCountryCode ? (
                                                            <CircleFlag
                                                                countryCode={phoneCountryCode}
                                                                className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] bg-gray-200 flex items-center justify-center shrink-0">
                                                                <CircleFlag
                                                                    countryCode="eu"
                                                                    className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full -translate-x-[3.6px] shrink-0"
                                                                />
                                                            </div>
                                                        )}
                                                        <div className="flex flex-col items-start min-w-0 flex-1">
                                                            <span className="font-medium text-left truncate w-full">
                                                                {phoneNumber.name}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground text-left truncate w-full">
                                                                {formattedNumber}
                                                            </span>
                                                        </div>
                                                    </Button>
                                                );
                                            })
                                        ) : (
                                            <div className="text-center text-muted-foreground py-4 text-sm">
                                                {searchTerm.trim()
                                                    ? t("no_search_results", { term: searchTerm.trim() })
                                                    : t("no_available_phone_number_found")}
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="flex justify-end pt-2 border-t mt-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => refetch()}
                                    disabled={isLoading}
                                    className="h-8"
                                >
                                    <Icon icon={TbRefresh} size={14} />
                                    <span className="ml-1 text-xs">{t("refresh")}</span>
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </SidebarMenuItem>
        </SidebarMenu>
    );
};
